import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { ACCESS, getAccessMask, hasAccess } from './access';
import cwLogo from '../images/CW_Logo-retina.png';

// Admin shell. Nav is grouped into the two SIP programs + Settings and each
// item is gated by the signed-in admin's SIPAdminAccessID bitmask (stored at
// login as admin_access_mask). Groups with no visible items disappear.
// NOTE: Drawing has no bit of its own in SIPAdminAccess yet — it rides with
// Announce Winners (32) until it gets one.
const NAV_GROUPS = [
    {
        header: "Leader's Safety Incentive Program",
        short: 'Leader',
        items: [
            { to: '/admin/welcome-letters', label: 'Welcome Letters', bit: ACCESS.WELCOME_LETTERS },
            { to: '/admin/supervisor-qual', label: 'Qualification Maintenance', bit: ACCESS.QUAL_SUPERVISORS },
            { to: '/admin/disbursement', label: 'Monthly Points', bit: ACCESS.MONTHLY_POINTS },
        ],
    },
    {
        header: "Employee's Safety Incentive Program",
        short: 'Employee',
        items: [
            { to: '/admin/employee-qual', label: 'Qualification Maintenance', bit: ACCESS.QUAL_EMPLOYEES },
            { to: '/admin/drawing', label: 'Drawing', bit: ACCESS.ANNOUNCE_WINNERS },
            { to: '/admin/winners', label: 'Announce Winners', bit: ACCESS.ANNOUNCE_WINNERS },
        ],
    },
    {
        header: 'Annual Swag Program',
        short: 'Annual',
        items: [
            { to: '/admin/annual/program-year', label: 'Program Year', bit: ACCESS.ANNUAL_SWAG },
            { to: '/admin/annual/load-employees', label: 'Load Employees', bit: ACCESS.ANNUAL_SWAG },
            { to: '/admin/annual/availability', label: 'Swag Availability', bit: ACCESS.ANNUAL_SWAG },
            { to: '/admin/annual/welcome-letters', label: 'Welcome Letters', bit: ACCESS.ANNUAL_SWAG },
            { to: '/admin/annual/reports', label: 'Order Reports', bit: ACCESS.ANNUAL_SWAG },
            { to: '/admin/annual/signoff', label: 'Sign-off Sheet', bit: ACCESS.ANNUAL_SWAG },
            { to: '/admin/annual/errors', label: 'Error Log', bit: ACCESS.ANNUAL_SWAG },
        ],
    },
    {
        header: 'Settings',
        short: 'Settings',
        items: [
            { to: '/admin/inventory', label: 'Inventory', bit: ACCESS.INVENTORY },
            { to: '/admin/swag-items', label: 'Swag Items', bit: ACCESS.INVENTORY },
            { to: '/admin/load-employees', label: 'Load Employees', bit: ACCESS.LOAD_EMPLOYEES },
            { to: '/admin/order-processing', label: 'Order Processing', bit: ACCESS.ORDER_PROCESSING },
            { to: '/admin/reports', label: 'Historic Reports', bit: ACCESS.HISTORIC_REPORTS },
            { to: '/admin/rights', label: 'Rights Management', bit: ACCESS.RIGHTS_MANAGEMENT },
        ],
    },
];

function AdminLayout() {
    const navigate = useNavigate();
    const loc = useLocation();
    const first = localStorage.getItem('admin_first_name') || '';
    const last = localStorage.getItem('admin_last_name') || '';
    const displayName = `${first} ${last}`.trim();
    const mask = getAccessMask();

    const handleLogout = () => {
        ['admin_auth_token', 'admin_employee_id', 'admin_first_name', 'admin_last_name',
            'admin_access_visibility', 'admin_access_mask']
            .forEach((k) => localStorage.removeItem(k));
        navigate('/admin/login');
    };

    const navLink = (to, label) => {
        const active = loc.pathname === to || loc.pathname.startsWith(to + '/');
        return (
            <Link key={to} to={to} className={`sipnav-link${active ? ' is-active' : ''}`}>
                {label}
            </Link>
        );
    };

    const visibleGroups = NAV_GROUPS
        .map((g) => ({ ...g, items: g.items.filter((it) => hasAccess(it.bit, mask)) }))
        .filter((g) => g.items.length > 0);

    // Top tabs = the groups; clicking one activates its submenu (persists).
    // Defaults to the group that owns the current route.
    const routeGroup = visibleGroups.findIndex((g) =>
        g.items.some((it) => loc.pathname === it.to || loc.pathname.startsWith(it.to + '/')));
    const [openGroup, setOpenGroup] = useState(null);
    const activeIdx = openGroup != null && openGroup < visibleGroups.length
        ? openGroup
        : (routeGroup >= 0 ? routeGroup : 0);
    const activeGroup = visibleGroups[activeIdx];

    return (
        <div className="admin-page" style={styles.page}>
            <style>{ADMIN_PRINT_CSS}</style>
            <header className="admin-chrome sipnav">
                <div className="sipnav-top">
                    <div className="sipnav-brand">
                        <img src={cwLogo} alt="CW" className="sipnav-logo" />
                        <span className="sipnav-brand-sub">Administration</span>
                    </div>
                    <nav className="sipnav-tabs">
                        {visibleGroups.map((g, i) => (
                            <button
                                key={g.header}
                                onClick={() => setOpenGroup(i)}
                                className={`sipnav-tab${i === activeIdx ? ' is-active' : ''}`}
                                title={g.header}
                            >
                                {g.short || g.header}
                            </button>
                        ))}
                    </nav>
                    <div className="sipnav-user">
                        {displayName && <span className="sipnav-username">{displayName}</span>}
                        <button onClick={handleLogout} className="sipnav-signout">Sign out</button>
                    </div>
                </div>
                {activeGroup && (
                    <div className="sipnav-sub">
                        {activeGroup.items.map((it) => navLink(it.to, it.label))}
                    </div>
                )}
            </header>
            <main className="admin-main" style={styles.main}>
                <Outlet />
            </main>
        </div>
    );
}

// Nav chrome + the shared print rules. Classes (not inline styles) so we get
// hover states and a single place to tune the header.
const ADMIN_PRINT_CSS = `
.sipnav { background:#fff; border-bottom:1px solid #e5e7eb; padding:8px 20px 0; }
.sipnav-top { display:flex; align-items:center; gap:20px; }
.sipnav-brand { display:flex; align-items:center; gap:9px; flex:0 0 auto; }
.sipnav-logo { height:38px; width:auto; display:block; }
.sipnav-brand-sub { font-size:12px; font-weight:700; color:#9ca3af; letter-spacing:0.02em; }

.sipnav-tabs { display:flex; gap:4px; flex:1 1 auto; }
.sipnav-tab { background:none; border:none; padding:8px 16px; font-size:14px; font-weight:700; color:#4b5563;
    cursor:pointer; border-radius:8px 8px 0 0; border-bottom:3px solid transparent; transition:color .12s, background .12s; }
.sipnav-tab:hover { color:#111827; background:#f9fafb; }
.sipnav-tab.is-active { color:#15803d; border-bottom-color:#15803d; }

.sipnav-user { display:flex; align-items:center; gap:12px; font-size:12px; flex:0 0 auto; }
.sipnav-username { color:#374151; font-weight:600; }
.sipnav-signout { background:none; border:none; color:#dc2626; font-size:12px; font-weight:600; cursor:pointer; padding:0; }
.sipnav-signout:hover { text-decoration:underline; }

.sipnav-sub { display:flex; gap:2px; flex-wrap:wrap; padding:8px 0; margin-top:2px; border-top:1px solid #f3f4f6; }
.sipnav-link { font-size:13px; font-weight:600; color:#4b5563; text-decoration:none; padding:6px 12px;
    border-radius:6px; white-space:nowrap; transition:background .12s, color .12s; }
.sipnav-link:hover { background:#f3f4f6; color:#111827; }
.sipnav-link.is-active { background:#15803d; color:#fff; font-weight:700; }

@media (max-width: 720px) {
    .sipnav-top { flex-wrap:wrap; gap:10px; }
    .sipnav-tabs { order:3; flex-basis:100%; overflow-x:auto; }
}

@media print {
    .admin-chrome { display: none !important; }
    .admin-main   { padding: 0 !important; margin: 0 !important; max-width: none !important; }
    .admin-page   { background: #ffffff !important; min-height: 0 !important; }
    html, body    { background: #ffffff !important; }
    * { box-shadow: none !important; }
    @page { margin: 14mm; }
}`;

// Header/nav styling lives in ADMIN_PRINT_CSS (classes, so hover works).
const styles = {
    page: { minHeight: '100vh', background: '#f9fafb' },
    // Left-aligned (not centered) so wide grids don't sit in a narrow column
    // with a big empty margin on large monitors. maxWidth still caps line
    // length on ultrawide displays.
    main: { padding: '16px 20px', maxWidth: 1280, margin: 0 }
};

export default AdminLayout;
